import React, { useContext, useRef } from 'react';
import createStore from 'ctx-provider';
import managedPromise from 'lib/managedPromise';
import ConfirmModal from 'components/common/modals/ConfirmModal';
import feedbackCtx from 'context/feedback';

const useConfirm = () => {
	const { add, remove } = useContext(feedbackCtx);
	// one number: every confirm() renders its dialog under a new key, so a dialog shown right after another one
	// (same place, same component) is a new instance and cannot keep the earlier one's checkbox state
	const keyRef = useRef(0);

	/**
	 * Shows a confirmation modal with yes and no buttons.
	 * @param {string} message Modal content
	 * @param {object {message string, title string, severity ('success'|'error'|'warning'|'info'), checkbox {label string}, note string}}
	 * checkbox: an unchecked checkbox under the message; note: a line of text under the message
	 * @returns {Promise<bool|{checked: bool}>} false when no was clicked, true when yes was clicked; with a checkbox
	 * yes gives { checked } instead (an object, so `if (!answer)` still tells no from yes)
	 */
	const confirm = (message, options = {}) => {
		const promise = managedPromise();
		const withCheckbox = typeof options.checkbox?.label === 'string';

		const fulfill = value => {
			remove(promise.id);
			promise.fulfill(value);
		};

		const onYes = withCheckbox
			? checked => {
					fulfill({ checked: checked === true });
			  }
			: () => {
					fulfill(true);
			  };

		const onNo = () => {
			fulfill(false);
		};

		keyRef.current += 1;
		const content = (
			<ConfirmModal
				key={keyRef.current}
				title={options.title}
				message={message}
				onYes={onYes}
				onNo={onNo}
				severity={options.severity}
				checkbox={withCheckbox ? options.checkbox : undefined}
				note={typeof options.note === 'string' ? options.note : undefined}
			/>
		);

		promise.id = add({ content, backdrop: true }, options.severity);

		return promise;
	};

	return confirm;
};

const store = createStore(useConfirm);

export const { Provider } = store;
export default store.ctx;
